import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useEffect } from "react";
import { userApi, type UpdateUserRequest } from "~/api/user";
import { CLIENT_CONFIG } from "~/config/client";
import type { User } from "~/types/user";
import { devLog } from "~/lib/dev-log";
import { trackSignUp, trackUserIdentified } from "~/lib/track-events";
import * as Sentry from "@sentry/react";
import { ApiClientError, apiErrorMessage } from "~/api/client";
import { configureSessionFlow, requireLoginSession, warmUpSession } from "~/api/session";

interface UserState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  userDataReloaded: boolean;
  regionBlocked: boolean;
  /**
   * Start the session flow early. Purely a warm-up — `~/lib/session-gate` self-starts on
   * the first request that needs a session, so nothing breaks if this never runs.
   */
  initializeFromStorage: () => Promise<void>;

  // Auth actions (reload)
  signup: (email: string) => Promise<"authenticated" | "email-sent" | void>;
  finishDelayedSignup: (email: string, invitationCode?: string) => Promise<"authenticated" | "email-sent" | void>;
  emailLogin: (email: string, otpToken: string) => Promise<"authenticated" | void>;
  firebaseLogin: (
    idToken: string,
    email?: string,
    provider?: "facebook" | "google" | "google_one_tap",
    invitationCode?: string
  ) => Promise<"authenticated" | void>;
  appleLogin: (
    code: string,
    email?: string,
    firstName?: string,
    lastName?: string,
    invitationCode?: string
  ) => Promise<"authenticated" | void>;
  facebookLogin: (
    token: string,
    profile: string | object,
    type?: string,
    invitationCode?: string
  ) => Promise<"authenticated" | void>;
  logout: () => void;

  // User data actions
  updateUser: (data: UpdateUserRequest) => Promise<void>;

  // User data refresh
  getData: () => Promise<User>;

  // Utility actions
  clearError: () => void;

  // Atomic operation to check and set loading state
  trySetLoading: (reload?: boolean) => boolean;
  setNotLoading: () => void;
}

/**
 * Announce a user to everything outside the store: analytics and Sentry.
 *
 * Shared by the two paths that establish an identity — `publishLogin` for a user-initiated login
 * and `onSessionEstablished` for the bootstrap — because these calls must not drift apart.
 * Only this fan-out is shared: what each path writes into the store, and which signup label it
 * reports, genuinely differ and stay at the call sites.
 */
function identifyUserEverywhere(user: User): void {
  trackUserIdentified(user._id);
  Sentry.setUser({
    id: user._id,
    email: user.basicInfo?.email,
    username: user.basicInfo?.name,
  });
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => {
      // ============================================================================
      // Helper Functions
      // ============================================================================

      const isRegionBlockedError = (error: unknown): boolean =>
        error instanceof ApiClientError &&
        (error.code === "region_not_supported" || error.code === "country_not_supported");

      const isLimitedAccessUser = (user: User): boolean => user.accessState === "limited_access";

      /**
       * The failure twin of `publishLogin`. Extracted for the same reason: the Facebook handler had
       * drifted and stopped setting `error`, so its failures surfaced nothing to the UI. Six copies
       * of a block is six chances to forget one.
       */
      const failLogin = (error: unknown, what: string, fallback: string) => {
        console.error(`❌ ${what} failed:`, error);
        if (isRegionBlockedError(error)) set({ regionBlocked: true });
        set({ error: apiErrorMessage(error, fallback), isLoading: false });
      };

      /**
       * Publish a successful login: session gate first, then store, analytics and Sentry identity.
       *
       * One function rather than the same fifteen lines in all six handlers, so the order stays
       * fixed and a change to it cannot land in five places and be forgotten in the sixth.
       *
       * `requireLoginSession` comes first and throws if the session cookie is not the one this
       * response issued. Nothing below it may run in that case: a 200 means the backend made a
       * session, only the cookie proves this browser kept it, and publishing anyway would show the
       * new identity in the UI while every request authenticated as the stale (usually anonymous)
       * session still in the jar. Throwing puts it on the same path as every other login failure:
       * the catch below sets `error` and rethrows. Whether the user sees it depends on the entry
       * point — `LoginFlow` in `Login.tsx` passes no `onError`, so there it is dropped.
       */
      const publishLogin = (user: User, token: string, signupMethod: string) => {
        requireLoginSession(`user_login:${signupMethod}`, token);
        set({ user, token });
        if (isLimitedAccessUser(user)) set({ regionBlocked: true });
        // Identity BEFORE the event, always: GTM tags triggered by `sign_up` read `user_id` off the
        // dataLayer, so pushing the event first hands them the previous value. Reversing these two
        // lines silently breaks sign_up conversion attribution.
        identifyUserEverywhere(user);
        trackSignUp(signupMethod);
      };

      return {
        user: null,
        token: null,
        isLoading: false,
        error: null,
        userDataReloaded: false,
        regionBlocked: false,

        signup: async (email: string) => {
          if (!get().trySetLoading()) {
            devLog("Signup skipped - already loading");
            return;
          }
          try {
            const response = await userApi.signup({
              email,
              termsVersion: CLIENT_CONFIG.TERMS_VERSION,
            });
            get().setNotLoading();

            if ("user" in response && "token" in response) {
              publishLogin(response.user, response.token, "email");
              return "authenticated";
            } else {
              return "email-sent";
            }
          } catch (error) {
            failLogin(error, "Signup", "Failed to signup");
            throw error;
          }
        },

        finishDelayedSignup: async (email: string, invitationCode?: string) => {
          if (!get().trySetLoading()) {
            devLog("Finish delayed signup skipped - already loading");
            return;
          }
          try {
            const response = await userApi.finishDelayedSignup({
              email,
              termsVersion: CLIENT_CONFIG.TERMS_VERSION,
              invitationCode,
            });
            get().setNotLoading();

            if ("user" in response && "token" in response) {
              publishLogin(response.user, response.token, "email");
              return "authenticated";
            } else {
              return "email-sent";
            }
          } catch (error) {
            failLogin(error, "Finish delayed signup", "Failed to finish delayed signup");
            throw error;
          }
        },

        emailLogin: async (email: string, otpToken: string) => {
          if (!get().trySetLoading()) {
            devLog("Email login skipped - already loading");
            return;
          }
          try {
            const response = await userApi.emailLogin({
              email,
              token: otpToken,
              termsVersion: CLIENT_CONFIG.TERMS_VERSION,
            });
            get().setNotLoading();

            if ("user" in response && "token" in response) {
              publishLogin(response.user, response.token, "email");
              return "authenticated";
            }
          } catch (error) {
            failLogin(error, "Email login", "Failed to login");
            throw error;
          }
        },

        firebaseLogin: async (
          idToken: string,
          email?: string,
          provider?: "facebook" | "google" | "google_one_tap",
          invitationCode?: string
        ) => {
          if (!get().trySetLoading()) {
            devLog("Firebase login skipped - already loading");
            return;
          }
          try {
            const response = await userApi.firebaseLogin({ idToken, email, invitationCode });
            get().setNotLoading();

            if ("user" in response && "token" in response) {
              publishLogin(response.user, response.token, provider || "unknown_provider");
              return "authenticated";
            }
          } catch (error) {
            failLogin(error, "Firebase login", `Failed to login with ${provider}`);
            throw error;
          }
        },

        appleLogin: async (
          code: string,
          email?: string,
          firstName?: string,
          lastName?: string,
          invitationCode?: string
        ) => {
          if (!get().trySetLoading()) {
            devLog("Apple login skipped - already loading");
            return;
          }
          try {
            const response = await userApi.appleLogin({
              code,
              email,
              firstName,
              lastName,
              termsVersion: CLIENT_CONFIG.TERMS_VERSION,
              invitationCode,
            });
            get().setNotLoading();

            if ("user" in response && "token" in response) {
              publishLogin(response.user, response.token, "apple");
              return "authenticated";
            }
          } catch (error) {
            failLogin(error, "Apple login", "Failed to login with Apple");
            throw error;
          }
        },

        facebookLogin: async (token: string, profile: string | object, type?: string, invitationCode?: string) => {
          if (!get().trySetLoading()) {
            devLog("Facebook login skipped - already loading");
            return;
          }
          try {
            const response = await userApi.facebookLogin({ token, profile, type, invitationCode });
            get().setNotLoading();

            if ("user" in response && "token" in response) {
              publishLogin(response.user, response.token, "facebook");
              return "authenticated";
            }
          } catch (error) {
            failLogin(error, "Facebook login", "Failed to login with Facebook");
            throw error;
          }
        },

        initializeFromStorage: async () => {
          // The whole sequence — token_login, its fallback, and the ordering rules between
          // them — lives in `~/api/session.ts`. It used to live here as a bare
          // `catch { createAccount() }`, which is why a network blip discarded a working
          // token and manufactured a junk anonymous account.
          warmUpSession();
        },

        logout: () => {
          set({ user: null, token: null, userDataReloaded: false, error: null });
          trackUserIdentified(null);
          Sentry.setUser(null);
        },

        updateUser: async (data: UpdateUserRequest) => {
          if (!get().trySetLoading(true)) {
            devLog("🚫 Update user skipped - already loading");
            return;
          }
          try {
            const response = await userApi.updateUser(data);
            set({ user: response, isLoading: false });
            devLog("✅ User updated successfully:", response);
          } catch (error) {
            console.error("❌ User update failed:", error);
            set({
              error: apiErrorMessage(error, "Failed to update user"),
              isLoading: false,
            });
            throw error;
          }
        },

        getData: async () => {
          if (!get().trySetLoading(true)) {
            devLog("🚫 Get data skipped - already loading");
            return get().user!;
          }
          try {
            const response = await userApi.getData();
            set({ user: response, isLoading: false, userDataReloaded: true });
            devLog("✅ User data fetched successfully:", response);
            return response;
          } catch (error) {
            console.error("❌ Failed to fetch user data:", error);
            set({
              error: apiErrorMessage(error, "Failed to fetch user data"),
              isLoading: false,
            });
            throw error;
          }
        },

        clearError: () => set({ error: null }),

        // Atomic check-and-set operation for loading state
        trySetLoading: (reload = true) => {
          const state = get();
          if (state.isLoading) {
            return false; // Already loading, operation rejected
          }
          if (state.userDataReloaded && !reload) {
            return false; // Already loaded, operation rejected
          }
          set({ isLoading: true, error: null, userDataReloaded: reload });
          return true; // Successfully set to loading
        },

        setNotLoading: () => set({ isLoading: false }),
      };
    },
    {
      name: "user-storage",
      partialize: state => ({
        user: state.user,
        token: state.token,
      }),
    }
  )
);

/**
 * Hook to initialize user authentication on app start
 * Call this in your root component or app initialization
 */
export const useInitializeUser = (skip = false) => {
  const { initializeFromStorage, isLoading, error } = useUserStore();

  useEffect(() => {
    // Skipping is now genuinely harmless: the gate self-starts on the first request that
    // needs a session, so nothing has to be released to avoid wedging the page. This hook
    // only gets the work moving earlier.
    if (skip) return;
    initializeFromStorage().catch(console.error);
  }, [initializeFromStorage, skip]);

  return { isLoading, error };
};

// ============================================================================
// Wiring to the session flow
// ============================================================================
// The store owns token persistence (zustand `persist`), the flow owns sequencing, and the
// gate owns state. These three registrations are the seams between them.
//
// Reading the token through a callback rather than letting `api/session.ts` import this
// store keeps the dependency one-way (`store -> api -> lib`) with no cycle, and it means
// the token stays in the existing `user-storage` key — moving it would treat every current
// user as a first-time visitor on deploy and replace their account.

configureSessionFlow({
  readStoredToken: () => useUserStore.getState().token,

  // `regionBlocked` is recomputed here, not just in getData: it used to be derived from
  // every load's token_login response, and the optimistic path removed that. Without this
  // a limited_access visitor's block would go stale for up to a day.
  refreshUser: async () => {
    const user = await userApi.getData();
    useUserStore.setState({ user, userDataReloaded: true, regionBlocked: user.accessState === "limited_access" });
  },

  onSessionEstablished: (user, token, via) => {
    const typedUser = user as User;
    useUserStore.setState({
      user: typedUser,
      token,
      isLoading: false,
      userDataReloaded: true,
      error: null,
    });
    if (typedUser.accessState === "limited_access") {
      useUserStore.setState({ regionBlocked: true });
    }
    // Identity before the event — see the note in `publishLogin`.
    identifyUserEverywhere(typedUser);
    // Preserved verbatim from the actions this replaced: `create_account` reported
    // "anonymous" and `token_login` reported "token".
    trackSignUp(via === "create_account" ? "anonymous" : "token");
  },

  onSessionFailed: error => {
    if (
      error instanceof ApiClientError &&
      (error.code === "region_not_supported" || error.code === "country_not_supported")
    ) {
      useUserStore.setState({ regionBlocked: true });
    }
    // A session that definitively cannot exist must not leave a signed-in shell on screen. zustand
    // persist has already rehydrated `user`, so components keyed on it would render an authenticated
    // page while the gate is `unavailable` and every request fails. Guarding the NEW user from being
    // published was not enough; the OLD one was already there.
    //
    // Only for these two codes. Clearing on a transport failure is how a flaky connection used to
    // log people out — the same reason `onStoredTokenRejected` is reached only on a real refusal.
    // `token` is deliberately kept: it was never refused, only its cookie failed to land, so a later
    // successful bootstrap can still use it.
    if (
      error instanceof ApiClientError &&
      (error.code === "SESSION_COOKIE_NOT_STORED" || error.code === "SESSION_COOKIE_MISMATCH")
    ) {
      useUserStore.setState({ user: null });
    }
    useUserStore.setState({
      error: apiErrorMessage(error, "Failed to initialize user"),
      isLoading: false,
    });
  },

  onStoredTokenRejected: () => {
    // Only reached when the backend actually refused the token. A transport failure leaves
    // it alone — clearing it there is how a flaky connection used to log people out.
    useUserStore.setState({ user: null, token: null, userDataReloaded: false });
    trackUserIdentified(null);
    Sentry.setUser(null);
  },
});
